import { prefersReducedMotion } from '@theme/utilities';

/**
 * Fans the echo layers out, by one of two routes depending on the device.
 *
 * Where a pointer exists the echo follows it: open while the cursor is in the section,
 * closed once it leaves, repeatable. Where there is none there is nothing to react to,
 * so the echo opens by itself when the heading scrolls to the middle of the screen and
 * stays open, with the observer disconnecting after that one hit.
 *
 * Either way the element only toggles one class — all of the movement is a CSS
 * transition on transform and opacity, declared on the resting state so both directions
 * take the same time. Nothing is left running once the effect has played.
 */
class EchoText extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    // Leaving the class off keeps the ghosts collapsed and the heading static, which
    // is the right resting state for anyone who prefers reduced motion.
    if (prefersReducedMotion()) return;

    this.#controller = new AbortController();

    // Both paths toggle the same class, so the stylesheet knows nothing about this
    // split. The mode is settled once here rather than tracked: a tablet that gains a
    // trackpad mid-session keeps the behaviour the page started with, which is simpler
    // to reason about than swapping drivers underneath a running effect.
    if (window.matchMedia('(hover: hover)').matches) {
      this.#followPointer();
    } else {
      this.#revealOnScroll();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** Pointer devices open the echo while the cursor is in the section and close it after. */
  #followPointer() {
    // mouseenter/mouseleave don't bubble but do account for descendants, so listening
    // on the section covers everything inside it as one hover target.
    const target = this.closest('.section') ?? this;
    const signal = this.#controller?.signal;

    target.addEventListener('mouseenter', () => this.classList.add('echo-text--active'), { signal });
    target.addEventListener('mouseleave', () => this.classList.remove('echo-text--active'), { signal });
  }

  /**
   * Touch devices have no hover to react to, so the echo opens by itself once the
   * heading reaches the middle of the screen, and stays open from then on.
   */
  #revealOnScroll() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];

        if (!entry?.isIntersecting) return;

        this.classList.add('echo-text--active');

        // One shot: the echo stays open and nothing is left observing.
        this.#observer?.disconnect();
        this.#observer = null;
      },
      {
        // Squeezes the observed area down to a band across the middle of the viewport,
        // so this counts the heading arriving at the centre rather than its top edge
        // appearing at the bottom.
        rootMargin: '-45% 0px -45% 0px',
        threshold: 0,
      }
    );

    this.#observer.observe(this);
  }
}

if (!customElements.get('echo-text')) {
  customElements.define('echo-text', EchoText);
}
